import { Stack } from '@mui/material';
import type { ObjectStorageDestination } from '@repo/api-gw-sdk';
import { Provider, ResourceType } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { PreferencesKey } from '@/data/dal/preferencesKey';

import { StepContainer } from '../../wizard/StepContainer';
import { BucketSelectionStep } from '../bucketSelectionStep';
import type { RestoreS3State } from '../restoreS3Wizard';
import { StorageAccountSelectionStep } from '../storageAccountSelection';

export const S3Configuration = (props: StepProps<RestoreS3State>) => {
  const { wizardState: stepState, stepperLabels, back, finish } = props;
  const {
    S3,
    GCS,
    StorageAccount,
    prefix,
    restoreAccount,
    snapshot,
    resourceId,
  } = stepState;
  const dal = useDAL();
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const cloudProvider = restoreAccount?.restoreAccountAttributes.cloudProvider;
  const restoringToAws = cloudProvider === Provider.Aws;
  const restoringToAzure = cloudProvider === Provider.Azure;
  const restoringToGcp = cloudProvider === Provider.Gcp;

  const onNextClick = async () => {
    setError(undefined);
    setIsRestoring(true);
    const destination: ObjectStorageDestination =
      {} as unknown as ObjectStorageDestination;

    if (restoringToAws) {
      const bucketName = S3.Bucket ?? '';
      // for saving the bucket name between different restores
      void dal.preferences.updateUserPref(PreferencesKey.RestoreBucket, {
        bucketName,
      });
      destination.s3Bucket = { bucketName, prefix };
    } else if (restoringToGcp) {
      const bucketName = GCS.Bucket ?? '';
      // for saving the bucket name between different restores
      void dal.preferences.updateUserPref(PreferencesKey.RestoreGcsBucket, {
        bucketName,
      });
      destination.gcsBucket = { bucketName, prefix };
    } else if (restoringToAzure) {
      const accountName = StorageAccount.AccountName ?? '';
      const resourceGroup = StorageAccount.ResourceGroup ?? '';
      const container = StorageAccount.Container ?? '';

      destination.storageAccount = {
        name: accountName,
        container,
        resourceGroup,
        prefix,
      };
    }

    void dal.restore.s3Buckets
      .restore(resourceId, snapshot.id, {
        restoreAccountId: restoreAccount!.id,
        destination,
      })
      .then(() =>
        finish(
          `Started restore job for ${
            destination.s3Bucket?.bucketName ??
            destination.gcsBucket?.bucketName ??
            destination.storageAccount?.name
          }`
        )
      )
      .catch(async (e) => {
        if (e.message.includes('AzureTarget.ResourceGroup cannot be nil')) {
          // TODO(Benji) return error code
          setError(
            'Failed to restore into storage account: Storage account not found'
          );
        } else {
          const errorMessage = await extractErrorMessage(e);
          setError(`Failed to restore bucket: ${errorMessage}`);
        }
        setIsRestoring(false);
      });
  };

  const allowUsingSourceContainer =
    props.wizardState.resourceType === ResourceType.AzureStorageAccount;

  return (
    <StepContainer
      sx={{ padding: '0' }}
      stepperLabels={stepperLabels}
      stepperIndex={stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={back}
      canGoNext={() => {
        if (isRestoring) {
          return false;
        }

        if (restoringToAws) {
          return Boolean(S3.Bucket);
        } else if (restoringToGcp) {
          return Boolean(GCS.Bucket);
        } else if (restoringToAzure) {
          return Boolean(
            StorageAccount.AccountName &&
            (StorageAccount.Container || allowUsingSourceContainer)
          );
        }
        return false;
      }}
      navigationComponent={
        error && (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        )
      }
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      {restoringToAws && (
        <BucketSelectionStep
          {...props}
          preferencesKey={PreferencesKey.RestoreBucket}
        />
      )}
      {restoringToAzure && (
        <StorageAccountSelectionStep
          {...props}
          allowUsingSourceContainer={allowUsingSourceContainer}
        />
      )}
      {restoringToGcp && (
        <BucketSelectionStep
          {...props}
          preferencesKey={PreferencesKey.RestoreGcsBucket}
        />
      )}
    </StepContainer>
  );
};
